"use client"

import { useState, useEffect, useRef, useCallback } from "react"

// Constants
const BANNER_HEIGHT = 80
const NAV_HEIGHT = 48 // Navigation bar height above banner
const SCROLL_HIDE_THRESHOLD = 150
const SCROLL_SHOW_THRESHOLD = 100
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000 // 7 days
const TRANSITION_DURATION_MS = 300
const UNMOUNT_DELAY_MS = 350 // Slightly longer than transition to ensure completion

export function SmartAppBanner() {
	const [shouldRender, setShouldRender] = useState(false)
	const [isVisible, setIsVisible] = useState(false)
	const dismissedRef = useRef(false)
	const scrollListenerRef = useRef<(() => void) | null>(null)
	const prefersReducedMotionRef = useRef(false)

	// Mount: check conditions, schedule show
	useEffect(() => {
		// Detect reduced motion preference
		prefersReducedMotionRef.current = matchMedia("(prefers-reduced-motion: reduce)").matches

		const ua = navigator.userAgent
		const isMobile = /iPhone|iPod|Android/i.test(ua) || (navigator.maxTouchPoints > 1 && /Macintosh/.test(ua))
		if (!isMobile) return

		if ((navigator as any).standalone || matchMedia("(display-mode:standalone)").matches) return

		try {
			const ts = localStorage.getItem("outfame:sab")
			if (ts) {
				const n = parseInt(ts, 10)
				if (!isNaN(n) && Date.now() - n < DISMISS_COOLDOWN_MS) return
			}
		} catch {}

		// Android PWA install prompt
		const installHandler = (e: Event) => {
			e.preventDefault()
			;(window as any).__deferredInstallPrompt = e
		}
		window.addEventListener("beforeinstallprompt", installHandler)

		// Delay showing until page settles
		const show = () => {
			if (window.scrollY <= SCROLL_HIDE_THRESHOLD) {
				setShouldRender(true)
			}
		}

		if ("requestIdleCallback" in window) {
			const id = (window as any).requestIdleCallback(show, { timeout: 2000 })
			return () => {
				;(window as any).cancelIdleCallback(id)
				window.removeEventListener("beforeinstallprompt", installHandler)
			}
		} else {
			const t = setTimeout(show, 150)
			return () => {
				clearTimeout(t)
				window.removeEventListener("beforeinstallprompt", installHandler)
			}
		}
	}, [])

	// After banner mounts in DOM, animate in on next frame
	useEffect(() => {
		if (!shouldRender) return

		const showBanner = () => {
			setIsVisible(true)
			document.documentElement.style.setProperty("--smart-banner-h", `${BANNER_HEIGHT}px`)
		}

		// Skip animation for reduced motion
		if (prefersReducedMotionRef.current) {
			showBanner()
			return
		}

		let cancelled = false
		const raf = requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				if (!cancelled) showBanner()
			})
		})
		return () => {
			cancelled = true
			cancelAnimationFrame(raf)
		}
	}, [shouldRender])

	// Scroll show/hide
	useEffect(() => {
		if (!shouldRender) return

		let visible = true // Banner is visible after shouldRender becomes true

		const onScroll = () => {
			if (dismissedRef.current) return
			const y = window.scrollY
			if (y > SCROLL_HIDE_THRESHOLD && visible) {
				visible = false
				setIsVisible(false)
				document.documentElement.style.setProperty("--smart-banner-h", "0px")
			} else if (y <= SCROLL_SHOW_THRESHOLD && !visible) {
				visible = true
				setIsVisible(true)
				document.documentElement.style.setProperty("--smart-banner-h", `${BANNER_HEIGHT}px`)
			}
		}

		window.addEventListener("scroll", onScroll, { passive: true })
		scrollListenerRef.current = onScroll
		return () => window.removeEventListener("scroll", onScroll)
	}, [shouldRender])

	// Cleanup CSS variable on unmount
	useEffect(() => {
		return () => {
			document.documentElement.style.setProperty("--smart-banner-h", "0px")
		}
	}, [])

	const handleDismiss = useCallback(() => {
		dismissedRef.current = true
		setIsVisible(false)
		document.documentElement.style.setProperty("--smart-banner-h", "0px")
		try {
			localStorage.setItem("outfame:sab", String(Date.now()))
		} catch {}
		if (scrollListenerRef.current) {
			window.removeEventListener("scroll", scrollListenerRef.current)
			scrollListenerRef.current = null
		}
		// Remove from DOM after transition completes
		const delay = prefersReducedMotionRef.current ? 0 : UNMOUNT_DELAY_MS
		setTimeout(() => setShouldRender(false), delay)
	}, [])

	if (!shouldRender) {
		return <div />
	}

	const reducedMotion = prefersReducedMotionRef.current

	return (
		<>
			<div
				id="smart-app-banner"
				role="complementary"
				aria-label="Install Outfame app"
				data-nosnippet=""
				style={{
					position: "fixed",
					left: 0,
					right: 0,
					top: NAV_HEIGHT,
					height: BANNER_HEIGHT,
					zIndex: 9999998,
					display: "flex",
					alignItems: "center",
					padding: "0 12px",
					background: "#f2f2f2",
					borderBottom: "0.5px solid rgba(0,0,0,0.12)",
					transform: isVisible ? "translateY(0)" : `translateY(-${BANNER_HEIGHT}px)`,
					opacity: isVisible ? 1 : 0,
					transition: reducedMotion
						? "none"
						: `transform ${TRANSITION_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${TRANSITION_DURATION_MS}ms ease`,
				}}
			>
				{/* Close */}
				<button
					id="smart-banner-close"
					aria-label="Close app install banner"
					onClick={handleDismiss}
					style={{
						width: 44,
						height: 44,
						flexShrink: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						background: "none",
						border: "none",
						cursor: "pointer",
					}}
				>
					<svg width="10" height="10" viewBox="0 0 10 10" fill="none">
						<path d="M1 1l8 8M9 1l-8 8" stroke="#8e8e93" strokeWidth="1.5" strokeLinecap="round" />
					</svg>
				</button>

				{/* App icon */}
				<img
					src="/img/icons/smart-banner-icon.webp"
					alt="Outfame app icon"
					width={60}
					height={60}
					style={{ flexShrink: 0, borderRadius: 13, boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}
				/>

				{/* App info */}
				<div style={{ flex: 1, minWidth: 0, marginLeft: 10 }}>
					<div
						style={{
							fontSize: 15,
							fontWeight: 600,
							color: "#000",
							lineHeight: 1.2,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						Outfame - Instagram Growth
					</div>
					<div style={{ fontSize: 12, color: "#8e8e93", lineHeight: 1.2, marginTop: 2 }}>On the App Store</div>
					<div style={{ display: "flex", alignItems: "center", gap: 2, marginTop: 2 }}>
						{[0, 1, 2, 3, 4].map((i) => (
							<svg key={i} viewBox="0 0 20 20" style={{ width: 10, height: 10, fill: "#FF9500" }}>
								<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
							</svg>
						))}
						<span style={{ fontSize: 11, color: "#8e8e93", marginLeft: 2 }}>(1.2K)</span>
					</div>
				</div>

				{/* Open button */}
				<button
					id="smart-banner-open"
					aria-label="Open Outfame app"
					style={{
						flexShrink: 0,
						borderRadius: 9999,
						background: "#007AFF",
						color: "#fff",
						fontSize: 15,
						fontWeight: 600,
						padding: "6px 20px",
						minHeight: 44,
						border: "none",
						cursor: "pointer",
					}}
				>
					OPEN
				</button>
			</div>
			<div id="smart-banner-modal-root" />
		</>
	)
}
